/**
 * The OS → workspace file-open pipeline, shared by every entry point that
 * hands us bytes from the user's disk: the installed PWA's `launchQueue`
 * (double-clicked files on Chromium), the desktop shell's buffered opens,
 * and drag-ins that want open-with-dedup semantics.
 *
 * Identity model (see also local-link-store.ts):
 *   1. device link (originId / file handle → contentId)  — "this exact file"
 *   2. CAS hash match (server, creator-scoped)           — "these exact bytes"
 *   3. upload                                            — genuinely new
 *
 * A linked file whose bytes differ from the cloud row goes through the
 * three-way `resolveSyncState` comparison; conflicts come back as an
 * `OpenConflict` whose continuations the UI invokes — this module performs no
 * navigation and renders nothing.
 */
import { osAssociationForFilename } from '@/lib/content/os-file-associations';
import { resolveSyncState } from './sync-state';
import {
  deleteLink,
  findLinkByHandle,
  getLinkByOrigin,
  linkKey,
  putLink,
  type LocalFileLink,
} from './local-link-store';
import { isDesktopShell, writeLocalFile } from './tauri-bridge';

export interface IncomingLocalFile {
  name: string;
  bytes: ArrayBuffer;
  /** `File.type` when the platform provided one (empty for vendor exts). */
  mimeHint?: string;
  /** Desktop shell: stable volume+inode identity. */
  originId?: string;
  /** Desktop shell: absolute path (display + write-back only). */
  path?: string;
  /** PWA: the launch handle (origin identity + re-read capability). */
  handle?: FileSystemFileHandle;
}

export type OpenErrorCode = 'signed-out' | 'too-large' | 'quota' | 'upload-failed';

export type OpenOutcome =
  | { kind: 'open'; contentId: string }
  | { kind: 'conflict'; conflict: OpenConflict }
  | { kind: 'error'; code: OpenErrorCode };

export interface OpenConflict {
  state: 'cloud-newer' | 'local-newer' | 'diverged';
  fileName: string;
  contentId: string;
  cloudName: string;
  /** Desktop only: the dialog may offer to overwrite the on-disk file. */
  canUpdateLocal: boolean;
  /** Open the workspace version; optionally sync it over the local file. */
  openCloud(updateLocalFile?: boolean): Promise<OpenOutcome>;
  /** Keep both: upload the local bytes as a new document and open that. */
  uploadAsCopy(): Promise<OpenOutcome>;
}

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

interface ResolveHashResponse {
  match: { contentId: string; displayName: string } | null;
  linked: { contentId: string; hash: string | null; displayName: string } | null;
}

async function resolveOnServer(
  hash: string,
  linkedContentId: string | null,
): Promise<ResolveHashResponse | 'signed-out' | null> {
  try {
    const res = await fetch('/api/content/resolve-hash', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ hash, linkedContentId: linkedContentId ?? undefined }),
    });
    if (res.status === 401) return 'signed-out';
    if (!res.ok) return null;
    return (await res.json()) as ResolveHashResponse;
  } catch {
    return null;
  }
}

async function uploadBytes(
  file: IncomingLocalFile,
  mime: string,
): Promise<{ id: string } | { error: OpenErrorCode }> {
  const form = new FormData();
  form.append('file', new File([file.bytes], file.name, { type: mime }));
  try {
    const res = await fetch('/api/content/upload', {
      method: 'POST',
      credentials: 'same-origin',
      body: form,
    });
    if (res.status === 401) return { error: 'signed-out' };
    if (res.status === 413) return { error: 'quota' };
    if (res.status === 400) return { error: 'too-large' };
    if (!res.ok) return { error: 'upload-failed' };
    const json = (await res.json()) as { id?: string };
    return json.id ? { id: json.id } : { error: 'upload-failed' };
  } catch {
    return { error: 'upload-failed' };
  }
}

function mimeFor(file: IncomingLocalFile): string {
  return file.mimeHint || osAssociationForFilename(file.name)?.mime || 'application/octet-stream';
}

async function saveLink(
  userId: string,
  file: IncomingLocalFile,
  contentId: string,
  lastSyncHash: string,
  existing?: LocalFileLink | null,
): Promise<void> {
  const originId = file.originId ?? existing?.originId ?? `handle:${crypto.randomUUID()}`;
  await putLink({
    key: linkKey(userId, originId),
    userId,
    originId,
    contentId,
    lastSyncHash,
    name: file.name,
    path: file.path ?? existing?.path,
    handle: file.handle ?? existing?.handle,
    updatedAt: Date.now(),
  });
}

/** Pull the cloud row's bytes over the on-disk file; true on success. */
async function syncCloudToDisk(contentId: string, path: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/content/${contentId}?download=1`, {
      credentials: 'same-origin',
    });
    if (!res.ok) return false;
    return await writeLocalFile(path, await res.arrayBuffer());
  } catch {
    return false;
  }
}

export async function processLocalFile(
  userId: string,
  file: IncomingLocalFile,
): Promise<OpenOutcome> {
  const diskHash = await sha256Hex(file.bytes);
  const mime = mimeFor(file);

  const link = file.originId
    ? await getLinkByOrigin(userId, file.originId)
    : file.handle
      ? await findLinkByHandle(userId, file.handle)
      : null;

  const resolved = await resolveOnServer(diskHash, link?.contentId ?? null);
  if (resolved === 'signed-out') return { kind: 'error', code: 'signed-out' };

  const uploadNew = async (): Promise<OpenOutcome> => {
    const up = await uploadBytes(file, mime);
    if ('error' in up) return { kind: 'error', code: up.error };
    await saveLink(userId, file, up.id, diskHash, link);
    return { kind: 'open', contentId: up.id };
  };

  // Server unreachable mid-flow: fall back to the dumbest safe behavior —
  // a plain upload attempt (which will surface its own error if that fails
  // too). Never block the open on dedup bookkeeping.
  if (resolved === null) return uploadNew();

  // 1. Device link — this exact file has a cloud twin.
  if (link && resolved.linked) {
    const cloud = resolved.linked;
    const state = resolveSyncState(diskHash, cloud.hash, link.lastSyncHash);

    if (state === 'clean') {
      // Refresh name/path/handle and the agreed hash in passing.
      await saveLink(userId, file, cloud.contentId, diskHash, link);
      return { kind: 'open', contentId: cloud.contentId };
    }

    const conflict: OpenConflict = {
      state,
      fileName: file.name,
      contentId: cloud.contentId,
      cloudName: cloud.displayName,
      canUpdateLocal: state !== 'local-newer' && isDesktopShell() && !!file.path,
      openCloud: async (updateLocalFile?: boolean) => {
        if (updateLocalFile && file.path && cloud.hash) {
          const synced = await syncCloudToDisk(cloud.contentId, file.path);
          if (synced) await saveLink(userId, file, cloud.contentId, cloud.hash, link);
        }
        return { kind: 'open', contentId: cloud.contentId };
      },
      uploadAsCopy: async () => {
        // The on-disk file's twin becomes the new copy (saveLink repoints the
        // same originId); the old row keeps living untouched in the library.
        return uploadNew();
      },
    };
    return { kind: 'conflict', conflict };
  }

  // Link points at a row that no longer exists (or changed owner) — forget it.
  if (link && !resolved.linked) {
    await deleteLink(link.key);
  }

  // 2. Exact bytes already in the library — open that copy, adopt the link.
  if (resolved.match) {
    await saveLink(userId, file, resolved.match.contentId, diskHash, link);
    return { kind: 'open', contentId: resolved.match.contentId };
  }

  // 3. Genuinely new.
  return uploadNew();
}
